import { alpha, createTheme } from '@mui/material/styles';
import Logo from '../Logo';

// =============================================================================
// Font strategy — two stacks, one per product surface
// =============================================================================
// Restack has two distinct surfaces with different typographic needs:
//
//   1. WEBSITE (re-stack.com)  → Inter, web-font, brand-consistent
//      Uses Inter as the primary face to land in the "premium AI tooling"
//      register the audience reads on Stripe/Anthropic/Linear/Vercel/Notion
//      daily. Same font on every device → consistent brand identity.
//
//   2. PLUGIN  (taskpane /chat)  → Fluent UI system stack, zero load
//      The taskpane lives inside PowerPoint, which itself renders with
//      Segoe UI on Windows and SF Pro on Mac. Matching that font in the
//      taskpane makes Restack feel like a native part of the host app,
//      not an embedded web view. This is Microsoft's official Fluent UI
//      recommendation for Office Add-ins. Zero font download = instant
//      taskpane render, which reinforces the "fast native editor"
//      positioning. The cross-platform "different font on different OS"
//      cost that hurts a marketing site is actually the FEATURE here —
//      the plugin should mirror its host.
//
// Both stacks are exported so non-theme callers (BlogArticleBody, html
// export templates, etc.) can pick the right one explicitly.
const WEB_SANS_STACK = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const PLUGIN_SANS_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';
// Backward-compat alias. Older imports of MODERN_SANS_STACK still resolve
// to the website stack (the previous behaviour) so nothing in the website
// codebase needs to change.
const MODERN_SANS_STACK = WEB_SANS_STACK;
const BRAND_SERIF_STACK = '"Playfair Display", "Georgia", "Times New Roman", serif';

// Accent colors — PowerPoint warm orange monochrome
const ORANGE_ACCENT = '#B83B1D';
const EMERALD_ACCENT = '#B83B1D';
const ORANGE_ACCENT_LIGHT = '#D4694E';
const EMERALD_ACCENT_LIGHT = '#D4694E';
// Muted terracotta for DARK surfaces only. The saturated #D4694E reads too
// hot against the deep warm-neutral console canvas; this desaturated, slightly
// deeper clay keeps the warmth but lets the background stay calm. Light mode is
// unchanged (it uses the colors above, which look right on cream).
const ORANGE_ACCENT_MUTED = '#C2705A';
const ORANGE_ACCENT_MUTED_LIGHT = '#D18A75';
const ORANGE_ACCENT_MUTED_DARK = '#A85844';
const ORANGE_ACCENT_RGB = '184, 59, 29';
const ORANGE_ACCENT_LIGHT_RGB = '212, 105, 78';
const EMERALD_ACCENT_RGB = '184, 59, 29';
const EMERALD_ACCENT_LIGHT_RGB = '212, 105, 78';

// Gradient definitions
const ACCENT_GRADIENT = `linear-gradient(135deg, ${ORANGE_ACCENT} 0%, ${EMERALD_ACCENT} 100%)`;
const ACCENT_GRADIENT_LIGHT = `linear-gradient(135deg, ${ORANGE_ACCENT_LIGHT} 0%, ${EMERALD_ACCENT_LIGHT} 100%)`;

export const getColors = (mode = 'dark', surface = 'web') => {
    // Orange accent palette (matching LandingPage)
    const warmAccent = mode === 'dark' ? ORANGE_ACCENT_LIGHT : ORANGE_ACCENT;
    const warmAccentRgb = mode === 'dark' ? ORANGE_ACCENT_LIGHT_RGB : ORANGE_ACCENT_RGB;
    const isConsole = surface === 'console';
    const isPlugin = surface === 'plugin';

    // Neutral border base — matches PowerPoint native taskpane
    const borderBase = mode === 'dark' ? '#909090' : '#666666';
    const dividerAlpha = mode === 'dark' ? 0.18 : 0.12;

    // Console surface: primary IS the brand rust (the landing-page register),
    // so primary-tinted chips, progress bars, and "act now" cards all read
    // warm instead of neutral gray.
    const primaryColors = isConsole
        ? (mode === 'dark'
            ? {
                main: ORANGE_ACCENT_MUTED,
                light: ORANGE_ACCENT_MUTED_LIGHT,
                dark: ORANGE_ACCENT_MUTED_DARK,
                contrastText: '#ffffff'
            }
            : {
                main: ORANGE_ACCENT,
                light: ORANGE_ACCENT_LIGHT,
                dark: '#9A2F15',
                contrastText: '#ffffff'
            })
        : mode === 'dark'
        ? {
            main: 'rgba(255, 255, 255, 0.9)',
            light: 'rgba(255, 255, 255, 1)',
            dark: 'rgba(255, 255, 255, 0.8)',
            contrastText: '#1e1e1e'
        }
        : {
            main: '#333333',
            light: '#555555',
            dark: '#222222',
            contrastText: '#ffffff'
        };

    // Warm accent palette - professional with subtle warmth. The console
    // uses real status hues (the landing-page set) so Active/Invited/paid
    // chips and meters read at a glance instead of as monochrome tints.
    const accentPalette = isConsole
        ? (mode === 'dark'
            ? {
                success: '#4CC38A',
                warning: '#E89B4B',
                error: '#F87171',
                info: 'rgba(245, 239, 234, 0.6)',
                premium: `rgba(${warmAccentRgb}, 0.9)`
            }
            : {
                success: '#16a34a',
                warning: '#D97706',
                error: '#DC2626',
                info: 'rgba(46, 20, 10, 0.6)',
                premium: `rgba(${warmAccentRgb}, 0.9)`
            })
        : mode === 'dark'
        ? {
            success: 'rgba(255, 255, 255, 0.75)',
            warning: `rgba(${warmAccentRgb}, 0.8)`,
            error: 'rgba(255, 100, 100, 0.9)',
            info: 'rgba(255, 255, 255, 0.6)',
            premium: `rgba(${warmAccentRgb}, 0.9)`
        }
        : {
            success: 'rgba(0, 0, 0, 0.7)',
            warning: `rgba(${warmAccentRgb}, 0.8)`,
            error: '#dc2626',
            info: 'rgba(0, 0, 0, 0.6)',
            premium: `rgba(${warmAccentRgb}, 0.9)`
        };

    // Accent bubble - with gradient support
    const accentBubbleBase = mode === 'dark' ? '#ffffff' : '#000000';
    const emeraldAccent = mode === 'dark' ? EMERALD_ACCENT_LIGHT : EMERALD_ACCENT;
    const emeraldAccentRgb = mode === 'dark' ? EMERALD_ACCENT_LIGHT_RGB : EMERALD_ACCENT_RGB;
    const accentBubble = {
        base: accentBubbleBase,
        tint: alpha(accentBubbleBase, mode === 'dark' ? 0.08 : 0.05),
        tintHover: alpha(accentBubbleBase, mode === 'dark' ? 0.12 : 0.08),
        border: alpha(accentBubbleBase, mode === 'dark' ? 0.2 : 0.15),
        text: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.85)',
        warm: warmAccent,
        warmRgb: warmAccentRgb,
        emerald: emeraldAccent,
        emeraldRgb: emeraldAccentRgb,
        gradient: mode === 'dark' ? ACCENT_GRADIENT_LIGHT : ACCENT_GRADIENT
    };

    // Backgrounds. Web/plugin: neutral, matched to the PowerPoint native
    // taskpane. Console: a neutral Material-style register (gray/white in
    // light, neutral near-black in dark) so the rust accent is reserved for
    // buttons, active states, and deliberate accents — the surfaces stay calm.
    const background = isConsole
        ? (mode === 'dark'
            ? {
                default: '#141414',  // neutral dark canvas
                paper: '#1E1E1E',    // surface, barely lifted
                card: '#242424',     // card
                lighter: '#2C2C2C'   // hover
            }
            : {
                default: '#FAFAFA',  // neutral page
                paper: '#FFFFFF',    // white cards/surfaces
                card: '#F4F4F5',     // subtle neutral card tint
                lighter: '#F7F7F8'   // neutral hover
            })
        : mode === 'dark'
        ? {
            default: '#1e1e1e',      // PPT dark canvas
            paper: '#2d2d2d',        // PPT dark surface
            card: '#333333',         // PPT dark card
            lighter: '#3c3c3c'       // PPT dark hover
        }
        : {
            default: '#f3f3f3',      // PPT light taskpane bg
            paper: '#ffffff',        // Pure white for cards/surfaces
            card: '#f5f5f5',         // PPT light card
            lighter: '#fafafa'       // PPT light hover
        };

    const text = mode === 'dark'
        ? (isConsole
            ? {
                // Neutral off-white over the neutral dark canvas.
                primary: '#ECECEC',
                secondary: 'rgba(236, 236, 236, 0.62)',
                disabled: 'rgba(236, 236, 236, 0.38)',
                hint: 'rgba(236, 236, 236, 0.5)'
            }
            : {
            primary: '#ffffff',
            secondary: 'rgba(255, 255, 255, 0.7)',
            disabled: 'rgba(255, 255, 255, 0.38)',
            hint: 'rgba(255, 255, 255, 0.5)'
        })
        : (isConsole
            ? {
                // Neutral near-black for the console light register.
                primary: '#1F1F1F',
                secondary: 'rgba(31, 31, 31, 0.62)',
                disabled: 'rgba(31, 31, 31, 0.38)',
                hint: 'rgba(31, 31, 31, 0.5)'
            }
            : {
            // Warm off-black for the web/plugin surface.
            primary: '#2E140A',
            secondary: 'rgba(46, 20, 10, 0.65)',
            disabled: 'rgba(46, 20, 10, 0.38)',
            hint: 'rgba(46, 20, 10, 0.5)'
        });

    // Console hairlines: neutral gray in both modes (no rust tint), so the
    // accent stays reserved for deliberate borders, not every divider.
    // Plugin hairlines are derived from the surface text color, the same base
    // the taskpane's resting border uses (see borderColorScale). The shared
    // `borderBase` gray is a different HUE from that warm off-black, so a
    // divider and a card border sitting in the same sidebar panel read as two
    // unrelated lines. Same hue, one step lighter than resting, so a separator
    // still reads quieter than an enclosing border.
    const divider = isConsole
        ? (mode === 'dark'
            ? 'rgba(255, 255, 255, 0.12)'
            : 'rgba(0, 0, 0, 0.10)')
        : isPlugin
            ? alpha(text.primary, mode === 'dark' ? 0.16 : 0.1)
            : alpha(borderBase, dividerAlpha);

    return {
        primary: primaryColors,
        accent: accentPalette,
        background,
        text,
        divider,
        // Console hover/selected overlays are NEUTRAL gray (no rust tint), so
        // accent is reserved for buttons and deliberate accents. The web/plugin
        // surface keeps its warm rust-tinted overlays.
        action: isConsole
            ? (mode === 'dark'
                ? {
                    active: 'rgba(236, 236, 236, 0.8)',
                    hover: 'rgba(255, 255, 255, 0.05)',
                    selected: 'rgba(255, 255, 255, 0.09)',
                    disabled: 'rgba(236, 236, 236, 0.3)',
                    disabledBackground: 'rgba(255, 255, 255, 0.12)'
                }
                : {
                    active: 'rgba(31, 31, 31, 0.75)',
                    hover: 'rgba(0, 0, 0, 0.04)',
                    selected: 'rgba(0, 0, 0, 0.07)',
                    disabled: 'rgba(0, 0, 0, 0.26)',
                    disabledBackground: 'rgba(0, 0, 0, 0.12)'
                })
            : isPlugin
            ? {
                // Taskpane overlays are neutral for the same reason the console's
                // are: hovering a menu item is not an accent moment. With rust
                // overlays a dropdown row washed warm while the hand-rolled
                // sidebar rows right above it washed gray, so the same gesture
                // produced two different colors in one panel.
                active: mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(46, 20, 10, 0.75)',
                hover: alpha(text.primary, mode === 'dark' ? 0.06 : 0.05),
                selected: alpha(text.primary, mode === 'dark' ? 0.1 : 0.08),
                disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                disabledBackground: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
            }
            : mode === 'dark'
            ? {
                active: 'rgba(255, 255, 255, 0.8)',
                hover: `rgba(${emeraldAccentRgb}, 0.08)`,
                selected: `rgba(${emeraldAccentRgb}, 0.12)`,
                disabled: 'rgba(255, 255, 255, 0.3)',
                disabledBackground: 'rgba(255, 255, 255, 0.12)'
            }
            : {
                active: 'rgba(46, 20, 10, 0.75)',
                hover: `rgba(${emeraldAccentRgb}, 0.06)`,
                selected: `rgba(${emeraldAccentRgb}, 0.1)`,
                disabled: 'rgba(0, 0, 0, 0.26)',
                disabledBackground: 'rgba(0, 0, 0, 0.12)'
            },
        accentBubble
    };
};

export const colors = getColors('dark');
export const lightColors = getColors('light');

const buildTokens = (mode, palette, surface = 'web') => {
    const isConsole = surface === 'console';
    const typography = {
        titleSm: {
            fontSize: '15px',
            lineHeight: '22px',
            fontWeight: 600,
            letterSpacing: '-0.01em'
        },
        bodyMd: {
            fontSize: '13px',
            lineHeight: '20px',
            letterSpacing: '-0.005em'
        },
        bodySm: {
            fontSize: '12px',
            lineHeight: '18px',
            letterSpacing: '-0.002em'
        },
        labelXs: {
            fontSize: '11px',
            lineHeight: '16px',
            letterSpacing: '0.02em',
            fontWeight: 600
        }
    };

    const spacing = {
        quark: 0.25,
        nano: 0.5,
        xs: 1,
        sm: 1.5,
        md: 2,
        lg: 3,
        xl: 4
    };

    // Radius scale. The nesting hierarchy is outer-larger-than-inner:
    //   lg   composer / input surface (the single most prominent container)
    //   md   top-level cards, panels, dialogs
    //   sm   surfaces nested inside a card (rows, code blocks, tiles)
    //   xs   chips, badges, tags, inline labels
    // Fully rounded controls use `pill`; circular icon buttons use `circle`.
    // NOTE: always emit these as explicit pixel strings at callsites, e.g.
    // `${radii.md}px`. A bare number in an MUI `sx` prop is multiplied by
    // theme.shape.borderRadius, so a radius written as the number 16 resolves
    // to 256px, not 16px.
    const radii = {
        xs: 8,
        sm: 12,
        md: 16,
        lg: 20,
        pill: '999px',
        circle: '50%'
    };

    // Gradient accent colors for surfaces (warm orange monochrome)
    const warmAccentRgb = mode === 'dark' ? ORANGE_ACCENT_LIGHT_RGB : ORANGE_ACCENT_RGB;
    const coolAccentRgb = mode === 'dark' ? ORANGE_ACCENT_LIGHT_RGB : ORANGE_ACCENT_RGB;

    // Console surfaces are NEUTRAL (gray card fills + gray borders) so the rust
    // accent is reserved for buttons and deliberate accents. The web/plugin
    // surface keeps the warm rust-tinted surfaces.
    const neutralSurfaceRgb = mode === 'dark' ? '255, 255, 255' : '0, 0, 0';
    const surfaces = isConsole
        ? (mode === 'dark'
            ? {
                canvas: palette.background.default,
                card: `rgba(${neutralSurfaceRgb}, 0.03)`,
                raised: `rgba(${neutralSurfaceRgb}, 0.05)`,
                hover: `rgba(${neutralSurfaceRgb}, 0.06)`,
                border: `rgba(${neutralSurfaceRgb}, 0.12)`
            }
            : {
                canvas: palette.background.default,
                card: `rgba(${neutralSurfaceRgb}, 0.015)`,
                raised: `rgba(${neutralSurfaceRgb}, 0.03)`,
                hover: `rgba(${neutralSurfaceRgb}, 0.04)`,
                border: `rgba(${neutralSurfaceRgb}, 0.10)`
            })
        : mode === 'dark'
        ? {
            canvas: palette.background.default,
            card: `rgba(${coolAccentRgb}, 0.04)`,
            raised: `rgba(${coolAccentRgb}, 0.06)`,
            hover: `rgba(${coolAccentRgb}, 0.08)`,
            border: `rgba(${coolAccentRgb}, 0.16)`
        }
        : {
            canvas: palette.background.default,
            card: `rgba(${coolAccentRgb}, 0.03)`,
            raised: `rgba(${coolAccentRgb}, 0.05)`,
            hover: `rgba(${coolAccentRgb}, 0.06)`,
            border: `rgba(${coolAccentRgb}, 0.12)`
        };

    const borderBase = mode === 'dark' ? '#909090' : '#666666';
    const borderOpacities = {
        hairline: mode === 'dark' ? 0.12 : 0.07,
        subtle: mode === 'dark' ? 0.16 : 0.1,
        strong: mode === 'dark' ? 0.24 : 0.16
    };

    const elevation = mode === 'dark'
        ? {
            flat: 'none',
            hairline: '0px 0px 0px rgba(0, 0, 0, 0)',
            xs: '0px 6px 16px rgba(0, 0, 0, 0.18)',
            sm: '0px 12px 32px rgba(0, 0, 0, 0.26)',
            md: '0px 22px 55px rgba(0, 0, 0, 0.32)'
        }
        : {
            flat: 'none',
            hairline: '0px 0px 0px rgba(0, 0, 0, 0)',
            xs: '0px 4px 14px rgba(15, 23, 42, 0.12)',
            sm: '0px 12px 32px rgba(15, 23, 42, 0.16)',
            md: '0px 24px 54px rgba(15, 23, 42, 0.18)'
        };

    // Two widths only. 1px is the standard for every resting surface; 2px is
    // reserved for genuinely emphasized states (selected, actively running).
    const borderWidths = {
        standard: 1,
        emphasis: 2
    };

    // One resting border and one emphasis border, composed from the surface
    // text color so a card, a panel, and the composer all read as one family
    // in both light and dark. `resting` is the value the taskpane already used
    // for its most prominent surfaces (composer, question card), so adopting it
    // everywhere lifts the ad hoc faint borders up to match rather than
    // softening the surfaces that were already right.
    const borderColorScale = {
        resting: alpha(palette.text.primary, mode === 'dark' ? 0.24 : 0.16),
        emphasis: alpha(palette.text.primary, mode === 'dark' ? 0.4 : 0.3)
    };

    const borders = {
        hairline: `1px solid ${alpha(borderBase, borderOpacities.hairline)}`,
        subtle: `1px solid ${alpha(borderBase, borderOpacities.subtle)}`,
        strong: `1px solid ${alpha(borderBase, borderOpacities.strong)}`,
        transparent: '1px solid transparent',
        widths: borderWidths,
        colors: borderColorScale,
        resting: `${borderWidths.standard}px solid ${borderColorScale.resting}`,
        emphasis: `${borderWidths.emphasis}px solid ${borderColorScale.emphasis}`
    };

    // Brand gradients — neutral base matching PowerPoint
    const gradients = mode === 'dark'
        ? {
            soft: `linear-gradient(135deg, rgba(${warmAccentRgb}, 0.04) 0%, rgba(${coolAccentRgb}, 0.06) 100%)`,
            heroWarm: ['#2d2d2d', '#1e1e1e'],
            heroUpload: ['#303030', '#222222'],
            heroCollab: ['#333333', '#252525'],
            heroReady: ['#363636', '#282828']
        }
        : {
            soft: `linear-gradient(135deg, rgba(${warmAccentRgb}, 0.03) 0%, rgba(${coolAccentRgb}, 0.05) 100%)`,
            heroWarm: ['#f5f5f5', '#fafafa'],
            heroUpload: ['#f3f3f3', '#f8f8f8'],
            heroCollab: ['#f4f4f4', '#f9f9f9'],
            heroReady: ['#f2f2f2', '#f7f7f7']
        };

    return {
        typography,
        spacing,
        radii,
        surfaces,
        elevation,
        borders,
        gradients
    };
};

// surface: 'web' (re-stack.com — Inter) or 'plugin' (taskpane — Fluent UI
// system stack). Defaults to 'web' so any existing callsite that doesn't
// pass a surface gets the previous Inter behaviour unchanged.
export const createAppTheme = (mode = 'dark', surface = 'web') => {
    const palette = getColors(mode, surface);
    const tokens = buildTokens(mode, palette, surface);
    const isPlugin = surface === 'plugin';
    const SANS_STACK = isPlugin ? PLUGIN_SANS_STACK : WEB_SANS_STACK;
    const isConsole = surface === 'console';

    const theme = createTheme({
        palette: {
            mode,
            primary: palette.primary,
            background: palette.background,
            text: palette.text,
            divider: palette.divider,
            action: palette.action,
            accentBubble: palette.accentBubble,
            success: {
                main: palette.accent.success
            },
            warning: {
                main: palette.accent.warning
            },
            error: {
                main: palette.accent.error
            },
            info: {
                main: palette.accent.info
            }
        },
        typography: {
            fontFamily: SANS_STACK,
            button: {
                textTransform: 'none',
                fontWeight: 500,
                letterSpacing: '-0.01em'
            },
            h1: {
                fontWeight: 700,
                letterSpacing: '-0.025em',
                fontFamily: SANS_STACK
            },
            h2: {
                fontWeight: 700,
                letterSpacing: '-0.025em',
                fontFamily: SANS_STACK
            },
            h3: {
                fontWeight: 600,
                letterSpacing: '-0.02em',
                fontFamily: SANS_STACK
            },
            h4: {
                fontWeight: 600,
                letterSpacing: '-0.02em',
                fontFamily: SANS_STACK
            },
            h5: {
                fontWeight: 500,
                letterSpacing: '-0.01em',
                fontFamily: SANS_STACK
            },
            h6: {
                fontWeight: 500,
                letterSpacing: '-0.01em',
                fontFamily: SANS_STACK
            },
            // body1 was previously inheriting tokens.typography.titleSm
            // which carries fontWeight: 600 — that's why every default
            // <Typography> across the app rendered as visibly bold
            // (footer copy, blog body, captions, etc.) despite per-class
            // CSS saying 400. Body text variants now explicitly carry
            // fontWeight: 400 so headings remain the only bold register.
            body1: {
                ...tokens.typography.titleSm,
                fontFamily: SANS_STACK,
                fontWeight: 400
            },
            body2: {
                ...tokens.typography.bodyMd,
                fontFamily: SANS_STACK,
                fontWeight: 400
            },
            caption: {
                ...tokens.typography.bodySm,
                fontFamily: SANS_STACK,
                fontWeight: 400
            },
            overline: {
                ...tokens.typography.labelXs,
                fontFamily: SANS_STACK,
                fontWeight: 500
            }
        },
        shape: {
            borderRadius: tokens.radii.md
        },
        components: {
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: palette.background.paper,
                        borderRadius: tokens.radii.md,
                        boxShadow: tokens.elevation.hairline
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontWeight: 500,
                        textTransform: 'none',
                        boxShadow: tokens.elevation.flat,
                        // Console: one uniform button radius (8px) everywhere,
                        // so no callsite needs an inline borderRadius override.
                        borderRadius: isConsole ? tokens.radii.xs : tokens.radii.sm,
                        '&:hover': {
                            boxShadow: tokens.elevation.flat
                        }
                    },
                    contained: isConsole
                        ? {
                            // Console primary action = brand rust (the landing
                            // CTA register), both modes.
                            backgroundColor: mode === 'dark' ? ORANGE_ACCENT_LIGHT : ORANGE_ACCENT,
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: mode === 'dark' ? '#C2563B' : '#9A2F15'
                            }
                        }
                        : {
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#333333',
                        color: mode === 'dark' ? '#1e1e1e' : '#ffffff',
                        '&:hover': {
                            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 1)' : '#222222'
                        }
                    },
                    outlined: isConsole
                        ? {
                            // Console secondary action = rust outline + rust
                            // label, so primary/secondary read as one family.
                            color: mode === 'dark' ? ORANGE_ACCENT_LIGHT : ORANGE_ACCENT,
                            borderColor: mode === 'dark' ? `rgba(${ORANGE_ACCENT_LIGHT_RGB}, 0.35)` : `rgba(${ORANGE_ACCENT_RGB}, 0.3)`,
                            '&:hover': {
                                borderColor: mode === 'dark' ? ORANGE_ACCENT_LIGHT : ORANGE_ACCENT,
                                backgroundColor: alpha(mode === 'dark' ? ORANGE_ACCENT_LIGHT : ORANGE_ACCENT, mode === 'dark' ? 0.1 : 0.06)
                            }
                        }
                        : isPlugin
                        ? {
                            // Secondary action in the taskpane is a NEUTRAL
                            // outline. A rust-outlined "Cancel" sitting beside a
                            // rust-filled "Save" made the two read as equal
                            // weight; the neutral outline lets the filled button
                            // carry the intent on its own.
                            borderColor: tokens.borders.colors.resting,
                            color: palette.text.primary,
                            '&:hover': {
                                borderColor: tokens.borders.colors.emphasis,
                                backgroundColor: palette.action.hover
                            }
                        }
                        : {
                        borderColor: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.2)` : `rgba(${EMERALD_ACCENT_RGB}, 0.2)`,
                        '&:hover': {
                            borderColor: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.35)` : `rgba(${EMERALD_ACCENT_RGB}, 0.35)`,
                            backgroundColor: palette.action.hover
                        }
                    }
                }
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: tokens.elevation.flat,
                        backdropFilter: 'blur(18px)',
                        borderBottom: tokens.borders.hairline
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: tokens.elevation.hairline,
                        border: tokens.borders.subtle,
                        borderRadius: tokens.radii.md,
                        backgroundColor: palette.background.paper
                    }
                }
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#333333'
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#555555'
                        }
                    }
                }
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderColor: palette.divider
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: palette.background.paper,
                        backgroundImage: 'none',
                        borderRadius: tokens.radii.md
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: isConsole
                        ? {
                            // Console inputs: match the 8px button radius so an
                            // input + button row aligns, neutral resting/hover
                            // border (Material register), rust only on focus.
                            '& .MuiOutlinedInput-root': {
                                borderRadius: `${tokens.radii.xs}px`,
                                '& fieldset': {
                                    borderColor: palette.divider
                                },
                                '&:hover fieldset': {
                                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.22)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'
                                }
                            }
                        }
                        : isPlugin
                        ? {
                            // Taskpane inputs rest on the SAME neutral border as
                            // every other calm surface in the pane (cards, the
                            // composer, panels), so a sidebar that stacks a
                            // dropdown, a search box, and a card no longer shows
                            // three different hues. Rust is reserved for focus,
                            // where it means "this is where you are typing"
                            // rather than decorating every field at rest.
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: tokens.borders.colors.resting
                                },
                                '&:hover fieldset': {
                                    borderColor: tokens.borders.colors.emphasis
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: mode === 'dark' ? EMERALD_ACCENT_LIGHT : EMERALD_ACCENT
                                }
                            }
                        }
                        : {
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.2)` : `rgba(${EMERALD_ACCENT_RGB}, 0.2)`
                            },
                            '&:hover fieldset': {
                                borderColor: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.3)` : `rgba(${EMERALD_ACCENT_RGB}, 0.3)`
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.5)` : EMERALD_ACCENT
                            }
                        }
                    }
                }
            },
            MuiCssBaseline: {
                styleOverrides: {
                    '*::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px'
                    },
                    '*::-webkit-scrollbar-track': {
                        background: 'transparent',
                        border: 'none'
                    },
                    '*::-webkit-scrollbar-thumb': {
                        // Orange-tinted scrollbar colors
                        background: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.2)` : `rgba(${EMERALD_ACCENT_RGB}, 0.15)`,
                        borderRadius: `${tokens.radii.lg}px`,
                        border: 'none',
                        '&:hover': {
                            background: mode === 'dark' ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.3)` : `rgba(${EMERALD_ACCENT_RGB}, 0.25)`
                        }
                    },
                    '*::-webkit-scrollbar-corner': {
                        background: 'transparent'
                    },
                    html: {
                        scrollbarWidth: 'thin',
                        scrollbarColor: mode === 'dark'
                            ? `rgba(${EMERALD_ACCENT_LIGHT_RGB}, 0.2) transparent`
                            : `rgba(${EMERALD_ACCENT_RGB}, 0.15) transparent`
                    }
                }
            }
        }
    });

    theme.typography.brandSerif = BRAND_SERIF_STACK;

    theme.restack = {
        tokens,
        typography: {
            ...tokens.typography,
            brandSerif: BRAND_SERIF_STACK
        }
    };

    return theme;
};

const theme = createAppTheme('dark');

export const brandConfig = {
    name: 'Restack',
    tagline: 'AI-powered slide generation inside PowerPoint',
    icon: Logo,
    logoStyles: {
        main: {
            fontSize: 36,
            opacity: 0.9,
            transition: 'all 0.3s ease',
            borderRadius: '8px'
        },
        small: {
            fontSize: 24,
            opacity: 0.9,
            borderRadius: '6px'
        },
        placeholder: {
            fontSize: 48,
            opacity: 0.3,
            borderRadius: '10px'
        },
        favicon: {
            fontSize: 32,
            color: '#000000',
            borderRadius: '6px'
        }
    },
    getLogoStyles: (mode = 'main', customStyles = {}) => {
        const baseStyles = brandConfig.logoStyles[mode] || brandConfig.logoStyles.main;
        return { ...baseStyles, ...customStyles };
    }
};

export const BRAND_SERIF_FONT = BRAND_SERIF_STACK;
// BRAND_SANS_FONT is the WEBSITE sans stack (Inter). All existing consumers
// of this export are website surfaces (blog body, html export templates,
// landing-page sx props) so this alias resolves to the web stack to keep
// website typography unchanged. New plugin-side code should import
// PLUGIN_SANS_FONT instead.
export const BRAND_SANS_FONT = WEB_SANS_STACK;
export const WEB_SANS_FONT = WEB_SANS_STACK;
export const PLUGIN_SANS_FONT = PLUGIN_SANS_STACK;

export default theme;
